import LinkIcon from "@mui/icons-material/Link";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import OpenInNew from "@mui/icons-material/OpenInNew";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { usePermission } from "core/hooks/usePermission";
import { useDmsDialogQueryState } from "features/DMS/hooks/useDmsDialogQueryState";
import { usePreloadDmsNode } from "features/DMS/hooks/usePreloadDmsNode";
import { DmsDocumentViewModal } from "features/DMS/pages/DmsLibraryPage/DmsDocumentViewModal";
import { getLibraryPath } from "features/DMS/utils/getLibraryPath";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ComponentErrorBoundary } from "../ComponentErrorBoundary";
import { ErrorComponent } from "../ErrorComponent";
import {
  GridAreaContainer,
  type GridAreaContainerProps,
} from "../GridAreaContainer";
import { QuickLinksWidgetSettingsDialog } from "./QuickLinksWidgetSettingsDialog";

function Wrapper({
  children,
  containerProps,
}: {
  children: React.ReactNode;
  containerProps?: GridAreaContainerProps;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:QuickLinksWidget",
  });
  const canManage = usePermission("Fixhub.QuickLinks.Manage");

  const [dialogOpen, setDialogOpen] = useState(false);
  const SettingsButton = canManage ? (
    <>
      <IconButton
        onClick={() => {
          setDialogOpen(true);
        }}
      >
        <MoreHoriz />
      </IconButton>
      <QuickLinksWidgetSettingsDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      />
    </>
  ) : null;
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: "links" } }}>
      <GridAreaContainer
        {...containerProps}
        gridArea="links"
        title={t("gridAreaContainer", "Quick Links")}
        disableBottomSlot
        RightSlot={SettingsButton}
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function QuickLinksWidget({
  containerProps,
}: {
  containerProps?: GridAreaContainerProps;
}) {
  const locale = useLocale();
  const brcy = useBrcyCode();
  const preloadDoc = usePreloadDmsNode();
  const preloadLibrary = usePreloadDmsNode();
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:QuickLinksWidget",
  });
  const canManage = usePermission("Fixhub.QuickLinks.Manage");
  const { actionState, clearActionState, setActionState } =
    useDmsDialogQueryState();
  const { data, isError, isLoading } = useCentrixApi({
    path: "/api/app/quick-link",
    parameters: {
      query: {
        brcyCode: brcy,
        locale,
      },
    },
  });

  const links = useMemo(() => {
    if (!Array.isArray(data?.items)) {
      return [];
    }
    return data.items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [data?.items]);

  if (isLoading) {
    return (
      <Wrapper containerProps={containerProps}>
        <Skeleton variant="rectangular" height={200} />
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper containerProps={containerProps}>
        <Paper sx={{ p: 2 }}>
          <ErrorComponent apiError={isError} />
        </Paper>
      </Wrapper>
    );
  }
  if (!canManage && links.length === 0) {
    return null;
  }
  if (links.length === 0) {
    return (
      <Wrapper containerProps={containerProps}>
        <Paper sx={{ p: 2 }}>
          <Typography color="text.secondary">
            {t("noLinks", "No quick links available.")}
          </Typography>
        </Paper>
      </Wrapper>
    );
  }

  return (
    <Wrapper containerProps={containerProps}>
      <Paper sx={{ p: 1 }}>
        <List dense>
          {links.map((link) => {
            if (link.type === "CustomLink") {
              const url =
                link.customLink?.translations?.find(
                  (tr) => tr.locale === locale
                )?.url ?? link.customLink?.translations?.[0]?.url;
              return (
                <ListItem key={link.id} disablePadding>
                  <ListItemButton
                    component="a"
                    href={url ?? "#"}
                    target={link.customLink?.target ?? "_blank"}
                    rel="noopener noreferrer"
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <LinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={link.name} />
                  </ListItemButton>
                </ListItem>
              );
            }
            return (
              <ListItem key={link.id} disablePadding>
                <ListItemButton
                  onPointerEnter={() => {
                    if (!link.docNodePath) {
                      return;
                    }
                    const libraryPath = getLibraryPath(link.docNodePath!);
                    if (!libraryPath) {
                      return;
                    }
                    preloadLibrary(libraryPath, "Library", "view");
                    preloadDoc(link.docNodePath!, "Document", "view");
                  }}
                  onClick={() => {
                    setActionState({
                      action: "document-view",
                      actionPath: link.docNodePath!,
                    });
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <OpenInNew fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={link.name} />
                </ListItemButton>
              </ListItem>
            );
          })}
          <DmsDocumentViewModal
            isEditMode={false}
            path={actionState.actionPath ?? ""}
            open={actionState.action === "document-view"}
            onClose={clearActionState}
          />
        </List>
      </Paper>
    </Wrapper>
  );
}
